import { Device, types } from "mediasoup-client";
import { getConfig } from "../config";
import { SignalingSocket } from "./signalingSocket";
import { evaluateMediaSupport, MediaUnavailableError } from "./mediaSupport";
import { applyTrackToggle } from "./mediaToggle";
import { canProduceTrack, isTrackLive } from "./mediaTrackGuards";
import { runWithRetries } from "./consumeRetry";
import { decodeMediaTokenIds } from "./mediaToken";
import { buildCallConstraints } from "./mediaDevicePreferences";

// Media client — wraps mediasoup-client against the media-service signaling WS
// (docs/design/modules/04-web-client.md §6 / docs/design/modules/02-media-service.md §5).
// Implements the full handshake: Device.load → send/recv transports → produce
// camera+mic → consume peers. Bitrate is capped at the publisher (modules/02 §7);
// the SFU forwards H.264 without transcoding (CPU-only).

/**
 * Resolve mediaWsBaseUrl to an absolute WebSocket URL.
 *
 * Absolute ws:// or wss:// values are returned unchanged (local dev / explicit override).
 * A relative path (e.g. "/media-ws") is promoted to wss:// when the page is served over
 * HTTPS, or ws:// for plain HTTP, using the current browser origin — so the same config
 * works from any hostname that serves the app (LAN, VPN, internet tunnel, etc.).
 *
 * The optional `loc` parameter exists only for unit testing; production callers omit it
 * and the default evaluates to window.location at call time.
 */
export function resolveMediaWsUrl(
  url: string,
  loc: { protocol: string; host: string } = window.location
): string {
  if (/^wss?:\/\//i.test(url)) return url;
  const wsProtocol = loc.protocol === "https:" ? "wss:" : "ws:";
  return `${wsProtocol}//${loc.host}${url}`;
}

export interface MediaClientHandlers {
  onLocalStream?: (stream: MediaStream) => void;
  onRemoteTrack?: (peerId: string, track: MediaStreamTrack, kind: "audio" | "video", appData: Record<string, unknown>) => void;
  /** A remote producer we were consuming closed (e.g. the sharer stopped a screen share). */
  onRemoteTrackClosed?: (peerId: string, appData: Record<string, unknown>) => void;
  onPeerClosed?: (peerId: string) => void;
  /** The local screen share ended (browser-native "Stop sharing" or track end). */
  onLocalScreenShareEnded?: () => void;
}

export interface StartOptions {
  token: string;
  callId: string;
  peerId: string;
  /** Camera chosen on the readiness screen; browser default when omitted. */
  cameraDeviceId?: string;
  /** Microphone chosen on the readiness screen; browser default when omitted. */
  micDeviceId?: string;
  /** Start the camera track disabled when false (still produced, re-enable in call). Default true. */
  cameraEnabled?: boolean;
  /** Start the mic track disabled when false (still produced, re-enable in call). Default true. */
  micEnabled?: boolean;
}

export interface MediaClient {
  start(opts: StartOptions): Promise<void>;
  stop(): Promise<void>;
  toggleMic(on: boolean): void;
  toggleCamera(on: boolean): void;
  startScreenShare(): Promise<void>;
  stopScreenShare(): Promise<void>;
}

interface ConsumeResponse {
  id: string;
  producerId: string;
  kind: "audio" | "video";
  rtpParameters: types.RtpParameters;
  appData?: Record<string, unknown>;
}

interface ProducerInfo {
  peerId: string;
  producerId: string;
  kind: "audio" | "video";
  appData: Record<string, unknown>;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
}

function resolveAfter(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class MediasoupMediaClient implements MediaClient {
  private signaling?: SignalingSocket;
  private device?: Device;
  private sendTransport?: types.Transport;
  private recvTransport?: types.Transport;
  private localStream?: MediaStream;
  private micProducer?: types.Producer;
  private camProducer?: types.Producer;
  private screenProducer?: types.Producer;
  private screenTrack?: MediaStreamTrack;
  private screenEndedHandler?: () => void;
  private readonly consumers = new Map<string, types.Consumer>();
  // consumerId → the source peer + producer appData, so consumerClosed can be surfaced
  // with enough context for the UI to know which track (e.g. a screen share) went away.
  private readonly consumerMeta = new Map<string, { peerId: string; appData: Record<string, unknown> }>();
  // Track which producerIds have already been consumed. getExistingProducers runs before
  // the newProducer listener is registered, but a race can cause a producer to appear in
  // both; consuming the same producerId twice creates a duplicate track and a black panel.
  private readonly consumedProducerIds = new Set<string>();
  // Set to true by stop() so that an in-flight start() aborts silently rather than
  // surfacing "socket closed" as a user-visible error when the component unmounts.
  private closed = false;
  // Device selection + initial on/off state chosen on the readiness screen.
  private startOpts?: StartOptions;

  constructor(private readonly handlers: MediaClientHandlers) {}

  async start(opts: StartOptions): Promise<void> {
    this.startOpts = opts;
    const cfg = getConfig();
    // Prefer the callId/peerId carried inside the media token: those are the exact strings the
    // media-service verifies the websocket against. The opts values are derived from a userId
    // that crosses SignalR as a JSON number and loses precision above 2^53 (a 63-bit StableId
    // is rounded), which would make peerId mismatch the token and the WS be rejected. Fall back
    // to the opts values for dev/no-token paths.
    const tokenIds = decodeMediaTokenIds(opts.token);
    const callId = tokenIds.callId ?? opts.callId;
    const peerId = tokenIds.peerId ?? opts.peerId;
    const url =
      `${resolveMediaWsUrl(cfg.mediaWsBaseUrl)}/?callId=${encodeURIComponent(callId)}` +
      `&peerId=${encodeURIComponent(peerId)}&token=${encodeURIComponent(opts.token)}`;
    this.signaling = new SignalingSocket(url, (n, d) => this.onNotification(n, d));
    try {
      await this.signaling.open();
      if (this.closed) return;

      const routerRtpCapabilities = await this.signaling.request<types.RtpCapabilities>("getRouterRtpCapabilities");
      if (this.closed) return;
      this.device = new Device();
      await this.device.load({ routerRtpCapabilities });
      if (this.closed) return;

      await this.createSendTransport();
      if (this.closed) return;
      await this.createRecvTransport();
      if (this.closed) return;
      await this.produceLocalMedia();
      if (this.closed) return;

      // Consume producers already present (the other party joined first). This loop runs on
      // the later joiner - always the call receiver. A single producer failing to consume
      // (a transient recv-transport/producer readiness race) must NOT abort the whole call
      // and surface a fatal toast: retry each one, and if it still fails, skip it and keep
      // the rest of the call alive (a later newProducer for the same id can still recover).
      const existing = await this.signaling.request<ProducerInfo[]>("getExistingProducers");
      if (this.closed) return;
      for (const p of existing) {
        if (this.closed) return;
        await this.consumeResiliently(p.peerId, p.producerId, p.appData);
      }
    } catch (e) {
      // stop() was called while start() was in-flight: the socket close caused a pending
      // request to reject. Swallow it — the component unmounted intentionally.
      if (this.closed) return;
      throw e;
    }
  }

  private async createSendTransport(): Promise<void> {
    const params = await this.signaling!.request<types.TransportOptions>("createWebRtcTransport", { direction: "send" });
    const transport = this.device!.createSendTransport(params);
    transport.on("connect", ({ dtlsParameters }, callback, errback) => {
      this.signaling!
        .request("connectTransport", { transportId: transport.id, dtlsParameters })
        .then(() => callback())
        .catch(errback);
    });
    transport.on("produce", ({ kind, rtpParameters, appData }, callback, errback) => {
      this.signaling!
        .request<{ id: string }>("produce", { transportId: transport.id, kind, rtpParameters, appData })
        .then(({ id }) => callback({ id }))
        .catch(errback);
    });
    this.sendTransport = transport;
  }

  private async createRecvTransport(): Promise<void> {
    const params = await this.signaling!.request<types.TransportOptions>("createWebRtcTransport", { direction: "recv" });
    const transport = this.device!.createRecvTransport(params);
    transport.on("connect", ({ dtlsParameters }, callback, errback) => {
      this.signaling!
        .request("connectTransport", { transportId: transport.id, dtlsParameters })
        .then(() => callback())
        .catch(errback);
    });
    this.recvTransport = transport;
  }

  private async produceLocalMedia(): Promise<void> {
    const cfg = getConfig();
    // getUserMedia only exists in a secure context (HTTPS / localhost). Fail with a clear
    // error instead of a raw "undefined.getUserMedia" TypeError + black panels.
    const support = evaluateMediaSupport();
    if (support !== "ok") throw new MediaUnavailableError(support);

    // Open the devices chosen on the readiness screen (default when unset). Adaptive video
    // range per design/09 §4 — quality steps down under transport-cc pressure.
    const stream = await navigator.mediaDevices.getUserMedia(
      buildCallConstraints({ cameraDeviceId: this.startOpts?.cameraDeviceId, micDeviceId: this.startOpts?.micDeviceId })
    );

    // stop() may have run while getUserMedia() was in flight. Discard the just-acquired
    // tracks silently and never publish them - publishing an ended track throws
    // "InvalidStateError: track ended" and surfaces a red toast with a black panel.
    if (this.closed) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this.localStream = stream;
    this.handlers.onLocalStream?.(stream);

    const audioTrack = stream.getAudioTracks()[0];
    const videoTrack = stream.getVideoTracks()[0];
    // Honor the readiness on/off choice: a track toggled off starts DISABLED but is still
    // produced, so the in-call mic/camera button can re-enable it without renegotiation.
    if (audioTrack && this.startOpts?.micEnabled === false) audioTrack.enabled = false;
    if (videoTrack && this.startOpts?.cameraEnabled === false) videoTrack.enabled = false;
    // Guard before each produce: the client could close, or a track could end, mid-startup.
    if (canProduceTrack(this.closed, audioTrack)) {
      this.micProducer = await this.sendTransport!.produce({ track: audioTrack, appData: { mediaTag: "mic" } });
    }
    // Re-check after the audio produce awaited above before touching the video track.
    if (canProduceTrack(this.closed, videoTrack)) {
      this.camProducer = await this.sendTransport!.produce({
        track: videoTrack,
        encodings: [{ maxBitrate: cfg.maxVideoBitrate }],
        codecOptions: { videoGoogleStartBitrate: 1000 },
        appData: { mediaTag: "cam" }
      });
    }
  }

  private onNotification(notification: string, data: Record<string, unknown>): void {
    switch (notification) {
      case "newProducer":
        void this.consumeResiliently(String(data.peerId), String(data.producerId), (data.appData as Record<string, unknown>) ?? {});
        break;
      case "consumerClosed": {
        const consumerId = String(data.consumerId);
        const producerId = typeof data.producerId === "string" ? data.producerId : undefined;
        const fallbackPeerId = typeof data.peerId === "string" ? data.peerId : "";
        const serverAppData = asRecord(data.appData);
        const meta = this.consumerMeta.get(consumerId);
        const consumer = this.consumers.get(consumerId);
        if (consumer) this.consumedProducerIds.delete(consumer.producerId);
        else if (producerId) this.consumedProducerIds.delete(producerId);
        consumer?.close();
        this.consumers.delete(consumerId);
        this.consumerMeta.delete(consumerId);
        const effectiveAppData = meta?.appData ?? serverAppData;
        if (effectiveAppData) this.handlers.onRemoteTrackClosed?.(meta?.peerId ?? fallbackPeerId, effectiveAppData);
        break;
      }
      case "peerClosed":
        this.handlers.onPeerClosed?.(String(data.peerId));
        break;
      default:
        break;
    }
  }

  // Consume `producerId`, tolerating transient failures: retry with backoff and, if it still
  // fails, give up on this one producer without throwing (so it never aborts call setup or
  // surfaces a fatal toast). Used by both the existing-producers loop and newProducer.
  private consumeResiliently(peerId: string, producerId: string, appData: Record<string, unknown>): Promise<boolean> {
    return runWithRetries(() => this.consume(peerId, producerId, appData), {
      shouldStop: () => this.closed,
      onGiveUp: (error) =>
        // Not fatal: the call continues with whatever else could be consumed.
        console.warn(`media: gave up consuming producer ${producerId} after retries`, error)
    });
  }

  private async consume(peerId: string, producerId: string, appData: Record<string, unknown>): Promise<void> {
    if (!this.recvTransport || !this.device || !this.signaling) return;
    if (this.consumedProducerIds.has(producerId)) return; // de-duplicate
    this.consumedProducerIds.add(producerId);
    try {
      const params = await this.signaling.request<ConsumeResponse>("consume", {
        producerId,
        rtpCapabilities: this.device.rtpCapabilities
      });
      // Prefer appData from the consume response (authoritative, server-resolved) over
      // the notification/existingProducers appData which can arrive empty in a race.
      const effectiveAppData = params.appData ?? appData;
      const consumer = await this.recvTransport.consume({
        id: params.id,
        producerId: params.producerId,
        kind: params.kind,
        rtpParameters: params.rtpParameters,
        appData: effectiveAppData
      });
      this.consumers.set(consumer.id, consumer);
      this.consumerMeta.set(consumer.id, { peerId, appData: effectiveAppData });
      await this.signaling.request("resumeConsumer", { consumerId: consumer.id });
      this.handlers.onRemoteTrack?.(peerId, consumer.track, params.kind, effectiveAppData);
    } catch (e) {
      // Roll back the de-dup marker so this producer can be retried (by the start() retry
      // loop, or a later newProducer for the same producer), then rethrow for the caller
      // to decide whether to retry or swallow.
      this.consumedProducerIds.delete(producerId);
      throw e;
    }
  }

  toggleMic(on: boolean): void {
    applyTrackToggle(on, this.micProducer, this.localStream?.getAudioTracks()[0]);
  }

  toggleCamera(on: boolean): void {
    applyTrackToggle(on, this.camProducer, this.localStream?.getVideoTracks()[0]);
  }

  async startScreenShare(): Promise<void> {
    if (!this.sendTransport) throw new Error("not in a call");
    // getDisplayMedia is likewise secure-context only.
    if (typeof navigator?.mediaDevices?.getDisplayMedia !== "function") {
      throw new MediaUnavailableError(evaluateMediaSupport());
    }
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    const track = stream.getVideoTracks()[0];
    // If the client closed while the picker was open, or the captured track is already
    // ended (user cancelled / immediately stopped), stop the stream and never produce -
    // producing an ended track throws "InvalidStateError: track ended".
    if (this.closed || !isTrackLive(track)) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    if (track) {
      this.screenTrack = track;
      this.screenProducer = await this.sendTransport.produce({ track, appData: { mediaTag: "screen", share: true } });
      // Browser-native "Stop sharing": remove the listener first so stopScreenShare() is
      // not re-entered from its own track.stop(), then close the producer, then notify UI.
      this.screenEndedHandler = () => {
        this.removeScreenEndedListener();
        void this.stopScreenShare().then(() => {
          this.handlers.onLocalScreenShareEnded?.();
        });
      };
      track.addEventListener("ended", this.screenEndedHandler);
    }
  }

  private removeScreenEndedListener(): void {
    if (this.screenTrack && this.screenEndedHandler) {
      this.screenTrack.removeEventListener("ended", this.screenEndedHandler);
    }
    this.screenEndedHandler = undefined;
  }

  async stopScreenShare(): Promise<void> {
    // Idempotent: nothing to do if already stopped.
    if (!this.screenProducer && !this.screenTrack) return;
    // Remove the ended listener before stopping the track so programmatic teardown
    // (app button, call stop) does not fire onLocalScreenShareEnded.
    this.removeScreenEndedListener();

    const producer = this.screenProducer;
    const track = this.screenTrack;
    const producerId = producer?.id;
    this.screenProducer = undefined;
    this.screenTrack = undefined;

    try {
      // Ask the server to close the producer before closing the local object. This keeps
      // the SFU producer id valid long enough to notify every remote consumer.
      if (producerId && this.signaling) {
        await Promise.race([this.signaling.request("closeProducer", { producerId }), resolveAfter(1500)]);
      }
    } catch {
      /* signaling already closing — server cleans up on disconnect */
    } finally {
      producer?.close();
      // Stop the captured display track so the browser's sharing indicator clears.
      track?.stop();
    }
  }

  async stop(): Promise<void> {
    this.closed = true;
    // Stop screen share first so closeProducer reaches the server before signaling closes.
    await this.stopScreenShare();
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.sendTransport?.close();
    this.recvTransport?.close();
    this.signaling?.close();
  }
}

export function createMediaClient(handlers: MediaClientHandlers = {}): MediaClient {
  return new MediasoupMediaClient(handlers);
}
